import { useState } from 'react';
import type { HistoryEntry } from '@medistock/contracts';
import { ApiError, api, localTime } from '../api';

/** My recent entries, and the only route staff have to change one (spec §5.3). */
export function History({
  entries,
  onChanged,
}: {
  entries: HistoryEntry[];
  onChanged: (message: string) => void;
}) {
  const [active, setActive] = useState<HistoryEntry | null>(null);

  return (
    <div className="list" data-testid="history">
      {entries.length === 0 ? <p className="notice info">Nothing recorded yet on this account.</p> : null}
      {entries.map((e) => (
        <div className="item" key={e.eventId} data-testid={`history-${e.eventId}`}>
          <div className="top">
            <span>
              {e.qty} {e.unit} {e.productName}
            </span>
            <span className={`badge ${e.type === 'WASTE' ? 'waste' : ''}`}>{e.type}</span>
          </div>
          <div className="sub">
            {localTime(e.createdAt)} · lot {e.lot}
            {e.reason ? ` · ${e.reason}` : ''}
          </div>
          <div className="row split" style={{ marginTop: 8 }}>
            {e.correction ? (
              <span className={`badge ${e.correction.status.toLowerCase()}`}>
                {e.correction.type} {e.correction.status}
              </span>
            ) : (
              <span className="badge">recorded</span>
            )}
            <button disabled={!e.correctable} onClick={() => setActive(e)}>
              Request change
            </button>
          </div>
        </div>
      ))}

      {active ? (
        <CorrectionSheet
          entry={active}
          onClose={() => setActive(null)}
          onDone={(m) => {
            setActive(null);
            onChanged(m);
          }}
        />
      ) : null}
    </div>
  );
}

function CorrectionSheet({
  entry,
  onClose,
  onDone,
}: {
  entry: HistoryEntry;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [requestType, setRequestType] = useState<'EDIT' | 'CANCEL'>('EDIT');
  const [qty, setQty] = useState(String(entry.qty));
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.correction({
        eventId: entry.eventId,
        requestType,
        requestedQty: requestType === 'EDIT' ? Number(qty) : undefined,
        reason: reason.trim(),
      });
      onDone('Request sent to a manager. Stock has not changed yet.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send the request');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sheet" role="dialog" aria-label="Request a change">
      <div className="panel">
        <div className="row split">
          <strong>
            Change {entry.qty} {entry.unit} {entry.productName}
          </strong>
          <button className="ghost" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <p className="small muted">
          Nothing moves in inventory until a manager approves this request.
        </p>

        <div className="row">
          <button
            className={requestType === 'EDIT' ? 'primary grow' : 'grow'}
            onClick={() => setRequestType('EDIT')}
            data-testid="correction-edit"
          >
            Correct the amount
          </button>
          <button
            className={requestType === 'CANCEL' ? 'danger grow' : 'grow'}
            onClick={() => setRequestType('CANCEL')}
            data-testid="correction-cancel"
          >
            Cancel the entry
          </button>
        </div>

        <div className="spacer" />
        {requestType === 'EDIT' ? (
          <div className="field">
            <label htmlFor="newqty">Correct amount ({entry.unit})</label>
            <input
              id="newqty"
              data-testid="correction-qty"
              inputMode="decimal"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
            />
          </div>
        ) : null}

        <div className="field">
          <label htmlFor="creason">Reason</label>
          <textarea
            id="creason"
            data-testid="correction-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why does this entry need to change?"
          />
        </div>

        {error ? (
          <p className="notice error" role="alert" data-testid="correction-error">
            {error}
          </p>
        ) : null}

        <button
          className="primary"
          data-testid="correction-submit"
          disabled={busy || reason.trim().length < 3}
          onClick={() => void submit()}
        >
          {busy ? 'Sending…' : 'Send request'}
        </button>
      </div>
    </div>
  );
}
